import React from 'react';
import {
  Cell as AriaCell,
  Column as AriaColumn,
  Row as AriaRow,
  Table as AriaTable,
  TableHeader as AriaTableHeader,
  Button,
  CellProps,
  Collection,
  ColumnProps,
  ColumnResizer,
  Group,
  ResizableTableContainer,
  RowProps,
  TableHeaderProps,
  TableProps,
  composeRenderProps,
  useTableOptions,
} from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';
import { Checkbox } from './Form/Checkbox';
import { composeTailwindRenderProps, focusRing } from '@/utils/utils';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';

export function Table(props: TableProps) {
  return (
    <ResizableTableContainer className="w-full">
      <AriaTable {...props} className="border-spacing-0 text-xs text-zinc-800" />
    </ResizableTableContainer>
  );
}

const columnStyles = tv({
  extend: focusRing,
  base: 'px-2 h-5 flex-1 flex gap-1 items-center overflow-hidden',
});

const resizerStyles = tv({
  extend: focusRing,
  base: 'w-px px-[8px] translate-x-[8px] box-content py-1 h-5 bg-clip-content bg-gray-400 dark:bg-zinc-500 forced-colors:bg-[ButtonBorder] cursor-col-resize rounded resizing:bg-blue-600 forced-colors:resizing:bg-[Highlight] resizing:w-[2px] resizing:pl-[7px] -outline-offset-2',
});

type ColumnWithRightContentProps = ColumnProps & { rightContent?: React.ReactNode };

export function Column({ rightContent, ...props }: ColumnWithRightContentProps) {
  return (
    <AriaColumn
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        '[&:hover]:z-20 [&:focus-within]:z-20 text-start font-bold cursor-default',
      )}>
      {composeRenderProps(props.children, (children, { allowsSorting, sortDirection }) => (
        <div className="flex items-center">
          <Group role="presentation" tabIndex={-1} className={columnStyles}>
            <div className="flex flex-1 items-center overflow-hidden">
              <span className="truncate">{children}</span>
              {allowsSorting && sortDirection === 'descending' && (
                <FaCaretDown className="inline-block ml-1 mt-[-2px]" data-cy="descending-sort" />
              )}
              {allowsSorting && sortDirection === 'ascending' && (
                <FaCaretUp className="inline-block ml-1 mt-[-2px]" data-cy="ascending-sort" />
              )}
            </div>
            {rightContent}
          </Group>
          {!props.width && <ColumnResizer className={resizerStyles} />}
        </div>
      ))}
    </AriaColumn>
  );
}

export function TableHeader<T extends object>(props: TableHeaderProps<T>) {
  const { selectionBehavior, selectionMode, allowsDragging } = useTableOptions();

  return (
    <AriaTableHeader
      {...props}
      className={twMerge('sticky top-0 z-10 text-left border-b border-zinc-200', props.className)}>
      {/* Add extra columns for drag and drop and selection. */}
      {allowsDragging && <Column />}
      {selectionBehavior === 'toggle' && (
        <AriaColumn
          width={80}
          minWidth={80}
          className="text-center text-sm font-semibold cursor-default p-2 flex justify-center">
          {selectionMode === 'multiple' && <Checkbox slot="selection" />}
        </AriaColumn>
      )}
      <Collection items={props.columns}>{props.children}</Collection>
    </AriaTableHeader>
  );
}

const rowStyles = tv({
  extend: focusRing,
  base: 'group/row relative cursor-default select-none -outline-offset-2 text-[#333333] disabled:text-gray-300 dark:text-zinc-200 dark:disabled:text-zinc-600 hover:bg-gray-100 dark:hover:bg-zinc-700/60 selected:bg-blue-100 selected:hover:bg-blue-200 dark:selected:bg-blue-700/30 dark:selected:hover:bg-blue-700/40 even:bg-white odd:bg-zinc-100 border-b border-zinc-200',
});

export function Row<T extends object>({ id, columns, children, ...otherProps }: RowProps<T>) {
  const { selectionBehavior, allowsDragging } = useTableOptions();

  return (
    <AriaRow id={id} {...otherProps} className={rowStyles}>
      {allowsDragging && (
        <Cell>
          <Button slot="drag">≡</Button>
        </Cell>
      )}
      {selectionBehavior === 'toggle' && (
        <Cell toggle className="">
          <Checkbox slot="selection" />
        </Cell>
      )}
      <Collection items={columns}>{children}</Collection>
    </AriaRow>
  );
}

const cellStyles = (toggle = false) =>
  tv({
    extend: focusRing,
    base: `py-6 px-2 w-auto ${toggle ? 'flex justify-center' : ''}`,
  });

interface ExtendedCellProps extends CellProps {
  toggle?: boolean;
}

export function Cell(props: ExtendedCellProps) {
  return <AriaCell {...props} className={cellStyles(props.toggle)} />;
}
